import useDebouncedCallback from 'hooks/useDebouncedCallback';
import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../Components/layouts";
import Typography from '@material-ui/core/Typography';
import GenericTable from '../../Components/GenericTable';
import { Paths } from '../../Routes/routePaths'
import { UserServices } from '../../Services/UserServices';
import { useImpersonation } from '../../hooks/useImpersonation';
import { Pagination } from '@material-ui/lab';
import OutlinedButton from 'Components/OutlinedButton';
import SearchBoxInput from 'Components/SearchBoxInput';
import { useHistory, useLocation } from 'react-router';
import commonStyles from 'theme/commonStyles';
import { userColumns } from './userColumns';
import OutlinedSelect from '../../Components/OutlinedSelect';
import { ProjectServices } from '../../Services/ProjectServices';
import { normalizeProgrammeOptions, YOUTH_PROGRAMME } from '../../utils/programmeOptions';

const userServices = new UserServices();
const projectServices = new ProjectServices();

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
        boxShadow: "0px 4px 30px rgba(12, 41, 84, 0.1)",
        borderRadius: 5,
        width: '100%',
        boxSizing: 'border-box',
        [theme.breakpoints.up('sm')]: {
            minHeight: 180,
        },
    },
    cardDetails: {
        display: 'flex',
        flexDirection: 'column',
    },
    content: {
        flex: '1 0 auto',
    },
    cover: {
        width: '100%',
        maxWidth: 174,
        height: 'auto',
        maxHeight: 132,
        paddingTop: 17,
        marginTop: 16,
        marginLeft: 0,
        borderRadius: 5,
        objectFit: 'contain',
        [theme.breakpoints.up('sm')]: {
            marginTop: 25,
            marginLeft: 20,
        },
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    cardTitle: {
        color: "#808080"
    },
    cardSubTitle: {
        fontFamily: 'Inter',
        fontStyle: 'normal',
        color: "#3D7DDD",
    },
    pageTitle: {
        marginBottom: theme.spacing(3),
        wordBreak: 'break-word',
    },
    filterGroup: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        width: '100%',
        [theme.breakpoints.down('xs')]: {
            alignItems: 'stretch',
        },
    },
    filterControl: {
        flex: '0 1 280px',
        width: 280,
        maxWidth: '100%',
        [theme.breakpoints.down('xs')]: {
            flexBasis: '100%',
            width: '100%',
        },
    },
    filterAction: {
        flex: '0 0 auto',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
}));

const Users = () => {

    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const commonClasses = commonStyles();
    const impersonation = useImpersonation();

    const [filterState, setFilterState] = React.useState(() => ({
        page: 1,
        search: new URLSearchParams(location.search).get('search') || '',
        programme_type: '',
        programme_subtype: '',
    }));
    const [administratorList, setUsersList] = React.useState([]);
    const [pagination, setPagination] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [programmeOptions, setProgrammeOptions] = React.useState(null);

    React.useEffect(() => {
        setLoading(true);
        projectServices.getProgrammeOptions()
            .then(result => {
                if (result?.status) setProgrammeOptions(normalizeProgrammeOptions(result));
            });
    }, []);

    const getAllUser = async (queryData) => {
        setLoading(true);
        const params = new URLSearchParams({
            search: queryData.search || '',
            page: String(queryData.page || 1),
        });
        if (queryData.programme_type) params.set('programme_type', queryData.programme_type);
        if (queryData.programme_type === YOUTH_PROGRAMME && queryData.programme_subtype) {
            params.set('programme_subtype', queryData.programme_subtype);
        }
        const query = params.toString();
        const userResult = await userServices.getAllUser(`main/userlist?${query}`);
        if (userResult && userResult.results) {
            setUsersList(userResult.results);
            setPagination(userResult.pagination);
        }
        setLoading(false);
    };

    const searchLater = useDebouncedCallback(() => {
        setFilterState(current => ({ ...current, page: 1 }));
        getAllUser({ ...filterState, page: 1 });
    }, 800);

    const handlePageChange = (evt, page) => {
        searchLater.cancel();
        setFilterState({ ...filterState, page });
        return getAllUser({ ...filterState, page });
    };

    const handleSearch = e => {
        e.preventDefault();
        searchLater.cancel();
        setFilterState(current => ({ ...current, page: 1 }));
        setLoading(true);
        return getAllUser({ ...filterState, page: 1 });
    };

    const handleProgrammeFilter = event => {
        searchLater.cancel();
        const { name, value } = event.target;
        const next = {
            ...filterState,
            page: 1,
            [name]: value,
            ...(name === 'programme_type' && value !== YOUTH_PROGRAMME ? { programme_subtype: '' } : {}),
        };
        setFilterState(next);
        setLoading(true);
        getAllUser(next);
    };

    const handleItemClick = (row, action) => {
        if (!row) return null;
        history.push(Paths.EditUsers + '/' + row.uuid);
    }

    const handleCreateNew = () => {
        if (impersonation.active) {
            return null;
        }
        history.push(Paths.AddUsers);
    }

    React.useEffect(() => {
        searchLater();
        return searchLater.cancel;
    }, [filterState.search, searchLater]);

    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    <Typography variant="h4" className={commonClasses.pageTitle}>Users</Typography>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item xs={12} md={4}>
                            <SearchBoxInput
                                placeholder="Search by name or email"
                                value={filterState.search}
                                handleChange={(evt) => {
                                    setFilterState({ ...filterState, search: evt.target.value })
                                    // return getAllUser({ search: evt.target.value, page: 1});
                                }}
                                handleSearch={handleSearch}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <div className={classes.filterGroup} data-testid="user-filter-group">
                                <div className={classes.filterControl}>
                                    <OutlinedSelect
                                        val={filterState.programme_type}
                                        name="programme_type"
                                        label="Programme type"
                                        options={(programmeOptions?.programmeTypes || []).map(option => ({ id: option.value, name: option.label }))}
                                        handleChange={handleProgrammeFilter}
                                        styleOverrides={{ marginTop: 0 }}
                                    />
                                </div>
                                {filterState.programme_type === YOUTH_PROGRAMME && (
                                    <div className={classes.filterControl}>
                                        <OutlinedSelect
                                            val={filterState.programme_subtype}
                                            name="programme_subtype"
                                            label="Sub-type"
                                            options={(programmeOptions?.programmeSubtypes || []).map(option => ({ id: option.value, name: option.label }))}
                                            handleChange={handleProgrammeFilter}
                                            styleOverrides={{ marginTop: 0 }}
                                        />
                                    </div>
                                )}
                                <div className={classes.filterAction}>
                                    <OutlinedButton
                                        title={"Create User"}
                                        handleClick={handleCreateNew}
                                        align={'right'}
                                        disabled={impersonation.active}
                                    />
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: 20 }}>
                        <GenericTable
                            columns={userColumns}
                            applyMinWidth={false}
                            loading={loading}
                            handleItemClick={handleItemClick}
                            list={administratorList}
                        />
                    </Grid>
                    <Grid container style={{ marginTop: 22 }} >
                        {pagination &&
                            <Pagination
                                page={filterState.page}
                                onChange={handlePageChange}
                                className={commonClasses.pagination}
                                count={parseInt(pagination?.total_pages || 0)}
                                variant="outlined"
                                shape="rounded"
                                siblingCount={0}
                            />}
                    </Grid>
                </div>
            </main>
        </Layout>
    );
}

export default Users;
